import { CloseCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Result, Typography } from 'antd';
import { ReactElement, ReactNode, useContext, useEffect } from 'react';
import {
  BrowserRouter as Router,
  useNavigate,
  useRoutes,
} from 'react-router-dom';
import { AppContext } from './AppContext';
import Error404 from './Error404';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { RoutePromptProvider } from './RoutePromptContext';
import { CategoryEnum } from './__generated__/graphql';
import App from './app/App';
import {
  ERROR_PAGE_SUBTITLE,
  ERROR_PAGE_TITLE,
  ROUTES,
} from './common/constants';
import LoaderComponent from './components/LoaderComponent';
import MaintenancePage from './components/MaintenancePage';
import VastuRules from './components/VastuRules';
import history from './historyData';
import Annotation from './modules/annotation-module/Annotation';
import Login from './modules/auth/Login';
import RefreshToken from './modules/auth/RefreshToken';
import Signup from './modules/auth/Register';
import Signout from './modules/auth/Signout';
import VerifyOtp from './modules/auth/VerifyOtp';
import { GET_CURRENT_USER } from './modules/auth/graphql/queries';
import DashboardWrapper from './modules/dashboard';
import Profile from './modules/profile/Profile';
import VastuReportPage from './modules/vastu-assessment-report/VastuReportPage';
import AnnotationFeatureScreen from './modules/vastu-assessment/AnnotationFeatureScreen';
import SelectEntrance from './modules/vastu-assessment/SelectEntrance';
import SelectNorthDirection from './modules/vastu-assessment/SelectNorthDirection';
import UploadFloorPlan from './modules/vastu-assessment/UploadFloorPlan';
import { AppContextType } from './types/appContext.type';

type FallbackProps = {
  error: Error | null;
  componentStack: string | null;
};

type ConditionalRouteProps = {
  condition: boolean;
  redirectTo: string;
  children: ReactNode;
};

const { Paragraph } = Typography;

const MyFallbackComponent = ({ error, componentStack }: FallbackProps) => (
  <Result
    status="error"
    title={ERROR_PAGE_TITLE}
    subTitle={ERROR_PAGE_SUBTITLE}
  >
    <div className="desc">
      <Paragraph>
        <Typography.Title level={4}> Error:</Typography.Title>
      </Paragraph>
      <Paragraph>
        <CloseCircleOutlined className="site-result-demo-error-icon" /> Your
        {error?.message?.toString()}
      </Paragraph>
      <Paragraph>
        <Typography.Title level={4}> Stacktrace:</Typography.Title>
      </Paragraph>
      <Paragraph>
        <CloseCircleOutlined className="site-result-demo-error-icon" /> Your
        {componentStack}
      </Paragraph>
    </div>
  </Result>
);
const checkVastuConditions = () => {
  const {
    state: { floorPlanSequence },
  } = useContext(AppContext) as AppContextType;
  const hasAngle = floorPlanSequence?.assets?.[0]?.angle !== null;
  const hasNorthDirection = !!floorPlanSequence?.assets?.find(
    (asset) => asset?.category === CategoryEnum.NorthDirected,
  );

  return {
    uploadFloorPlanSequenceExists: !!Object.keys(floorPlanSequence ?? {})
      .length,
    hasAngle,
    hasNorthDirection,
  };
};

const ConditionalRoute = ({
  condition,
  redirectTo,
  children,
}: ConditionalRouteProps): JSX.Element | null => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!condition) {
      navigate(redirectTo, { replace: true });
    }
  }, [condition, redirectTo, navigate]);

  return condition ? (children as ReactElement) : null;
};

const RoutesCollection = () => {
  const { uploadFloorPlanSequenceExists, hasNorthDirection } =
    checkVastuConditions();

  const AUTH_MODULES = [
    {
      path: ROUTES.LOGIN,
      element: <PublicRoute />,
      children: [{ path: ROUTES.LOGIN, element: <Login /> }],
    },
    {
      path: ROUTES.VERIFY_OTP,
      element: <PublicRoute />,
      children: [{ path: ROUTES.VERIFY_OTP, element: <VerifyOtp /> }],
    },
    {
      path: ROUTES.REGISTER,
      element: <PublicRoute />,
      children: [{ path: ROUTES.REGISTER, element: <Signup /> }],
    },

    {
      path: ROUTES.SIGNOUT,
      element: <PrivateRoute />,
      children: [{ path: ROUTES.SIGNOUT, element: <Signout /> }],
    },
    {
      path: ROUTES.AUTHENTICATION,
      element: <PrivateRoute />,
      children: [{ path: ROUTES.AUTHENTICATION, element: <RefreshToken /> }],
    },
  ];

  const DASHBOARD_MODULES = [
    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [{ path: ROUTES.MAIN, element: <DashboardWrapper /> }],
        },
      ],
    },
  ];

  const VASTU_ASSESSMENT_REPORT = [
    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [
            { path: ROUTES.VASTU_ANNOTATION, element: <Annotation /> },
          ],
        },
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [
            { path: ROUTES.VASTU_REPORT, element: <VastuReportPage /> },
          ],
        },
      ],
    },
  ];

  const USER_MODULES = [
    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [
            {
              path: ROUTES.PROFILE,
              element: <Profile />,
            },
          ],
        },
      ],
    },
  ];

  const VASTU_ASSESSMENT_MODULES = [
    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [
            {
              path: ROUTES.UPLOAD_FLOOR_PLAN,
              element: <UploadFloorPlan />,
            },
            {
              path: ROUTES.CHOOSE_DIRECTION,
              element: (
                <ConditionalRoute
                  condition={uploadFloorPlanSequenceExists}
                  redirectTo={ROUTES.UPLOAD_FLOOR_PLAN}
                >
                  <SelectNorthDirection />
                </ConditionalRoute>
              ),
            },
            {
              path: ROUTES.CHOOSE_ENTRANCE,
              element: (
                <ConditionalRoute
                  condition={hasNorthDirection}
                  redirectTo={ROUTES.UPLOAD_FLOOR_PLAN}
                >
                  <SelectEntrance />
                </ConditionalRoute>
              ),
            },
            {
              path: ROUTES.ANNOTATION_INFO,
              element: (
                <ConditionalRoute
                  condition={hasNorthDirection}
                  redirectTo={ROUTES.MAIN}
                >
                  <AnnotationFeatureScreen />
                </ConditionalRoute>
              ),
            },
          ],
        },
      ],
    },
  ];

  const VASTU_RULE_MODULES = [
    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [{ path: ROUTES.VASTU_RULE, element: <VastuRules /> }],
        },
      ],
    },
  ];

  const OTHER_MODULES = [
    {
      path: ROUTES.MAIN,
      element: <PrivateRoute />,
      children: [
        {
          path: ROUTES.MAIN,
          element: <App />,
          children: [
            {
              path: '*',
              element: <Error404 />,
            },
          ],
        },
      ],
    },
  ];

  const element = useRoutes([
    ...AUTH_MODULES,
    ...DASHBOARD_MODULES,
    ...VASTU_ASSESSMENT_REPORT,
    ...VASTU_ASSESSMENT_MODULES,
    ...VASTU_RULE_MODULES,
    ...USER_MODULES,
    ...OTHER_MODULES,
  ]);
  return element;
};

const RoutesWrapper = () => {
  const { getToken, initializeAuth } = useContext(AppContext) as AppContextType;
  const idToken = getToken();
  const path = window.location.pathname;

  const { loading } = useQuery(GET_CURRENT_USER, {
    skip: !idToken,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      initializeAuth(idToken, res?.getLoggedInUserWeb ?? null);
    },
    onError: () => {
      if (path !== ROUTES.AUTHENTICATION) {
        history?.push(ROUTES.AUTHENTICATION);
      }
    },
    nextFetchPolicy: 'network-only',
  });

  // use this variable from envs so that we can able to run maintenance page on runtime.
  const maintenance = process.env.REACT_APP_MAINTENANCE_ENABLE;
  if (loading) return <LoaderComponent />;
  return (
    <Sentry.ErrorBoundary fallback={MyFallbackComponent}>
      <Router>
        <RoutePromptProvider>
          {maintenance === 'true' ? <MaintenancePage /> : <RoutesCollection />}
        </RoutePromptProvider>
      </Router>
    </Sentry.ErrorBoundary>
  );
};
export default RoutesWrapper;
